import React, { useState, useEffect, useMemo } from 'react';
import {
    MapPin, Users, Settings as SettingsIcon, PoundSterling, Plus,
    Edit, Trash2, Eye, Power, PowerOff, X, Save, Info,
    Calculator, Ruler, ArrowRight, Search, ChevronLeft, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { cn } from '../utils/cn.js';
import APIENDPOINT from './config.js';
import GoogleInput from './GoogleInput.js';
export const Settings = () => {
    const [activeTab, setActiveTab] = useState('locations');

    const menu = [
        { id: 'locations', label: 'Locations', icon: MapPin },
        { id: 'drivers', label: 'Drivers', icon: Users },
        { id: 'price', label: 'Price Config', icon: PoundSterling },
        // { id: 'app', label: 'App Settings', icon: SettingsIcon },
    ];

    return (
        <div className="flex flex-col lg:flex-row gap-6 min-h-screen pb-20 lg:pb-10">
            {/* Sidebar - Horizontal scroll on mobile, Vertical on Desktop */}
            <div className="w-full lg:w-64 flex lg:flex-col overflow-x-auto lg:overflow-x-visible gap-2 pb-2 lg:pb-0 sticky top-0 bg-gray-50 z-10 lg:static">
                {menu.map((item) => (
                    <button
                        key={item.id}
                        onClick={() => setActiveTab(item.id)}
                        className={cn(
                            "flex-shrink-0 lg:w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all whitespace-nowrap",
                            activeTab === item.id
                                ? "bg-emerald-600 text-white shadow-lg"
                                : "bg-white text-gray-600 border border-gray-100"
                        )}
                    >
                        <item.icon className="w-5 h-5" />
                        {item.label}
                    </button>
                ))}
            </div>

            {/* Content Area */}
            <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-100 p-4 md:p-6">
                {activeTab === 'locations' && <LocationMaster />}
                {activeTab === 'drivers' && <DriverManager />}
                {activeTab === 'price' && <PriceList />}
                {activeTab === 'app' && <AppSettings />}
            </div>
        </div>
    );
};

// --- REUSABLE SEARCH & PAGINATION COMPONENT ---
const TableControls = ({ searchTerm, setSearchTerm, onAdd, addLabel }) => (
    <div className="flex flex-col md:flex-row gap-4 mb-6 justify-between items-center">
        <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
                type="text"
                placeholder="Search..."
                className="w-full pl-10 pr-4 py-2 border rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
        </div>
        <button onClick={onAdd} className="w-full md:w-auto bg-emerald-600 text-white px-6 py-2 rounded-xl font-bold flex items-center justify-center gap-2">
            <Plus className="w-5 h-5" /> {addLabel}
        </button>
    </div>
);

const Pagination = ({ totalItems, itemsPerPage, currentPage, setCurrentPage }) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    if (totalPages <= 1) return null;

    return (
        <div className="flex justify-center items-center gap-4 mt-6">
            <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => p - 1)}
                className="p-2 border rounded-lg disabled:opacity-30"
            >
                <ChevronLeft />
            </button>
            <span className="text-sm font-bold">Page {currentPage} of {totalPages}</span>
            <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(p => p + 1)}
                className="p-2 border rounded-lg disabled:opacity-30"
            >
                <ChevronRight />
            </button>
        </div>
    );
};

// --- LOCATION MASTER ---
const LocationMaster = () => {
    const [locations, setLocations] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [showModal, setShowModal] = useState(false);

    const [formData, setFormData] = useState({
        primary_name: 'Airport',
        custom_primary: '',
        location_name: '',
        address: '',
        latitude: '',
        longitude: ''
    });
    const itemsPerPage = 5;

    useEffect(() => { fetchLocations(); }, []);

    const fetchLocations = async () => {
        const res = await axios.get(`${APIENDPOINT}master.php?action=list`);
        setLocations(res.data);
    };

    const filteredData = locations.filter(loc =>
        loc.location_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        loc.primary_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const currentTableData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const deleteLocation = async (id) => {
        if (!window.confirm("Delete location?")) return;
        await axios.get(`${APIENDPOINT}master.php?action=delete&id=${id}`);
        fetchLocations();
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        let final_primary = formData.primary_name;
        if (final_primary === 'Other') {
            final_primary = formData.custom_primary?.trim();
        }

        const payload = {
            primary_name: final_primary || "",
            location_name: formData.location_name?.trim() || "",
            latitude: formData.latitude,
            longitude: formData.longitude,
            address: formData.address?.trim() || ""
        };

        if (formData.id) payload.id = formData.id;

        if (!payload.primary_name || !payload.location_name || !payload.latitude || !payload.address) {
            toast.error("Please select an address using the Google search suggestion");
            return;
        }

        try {
            const res = await axios.post(`${APIENDPOINT}master.php?action=save`, payload);
            if (res.data.status) {
                setShowModal(false);
                fetchLocations();
                toast.success("Location saved successfully");
            }
        } catch (err) {
            toast.error("Error saving location");
        }
    };

    return (
        <div>
            <TableControls
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                onAdd={() => {
                    setFormData({
                        id: null,
                        primary_name: 'Airport',
                        latitude: '',
                        longitude: '',
                        location_name: '',
                        address: '',
                        custom_primary: ''
                    });
                    setShowModal(true);
                }}
                addLabel="Add Location"
            />

            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-gray-50 text-gray-500 text-xs font-bold uppercase">
                        <tr>
                            <th className="p-4">Category</th>
                            <th className="p-4">Name</th>
                            <th className="p-4">Lat/Lng</th>
                            <th className="p-4">Address</th>
                            <th className="p-4 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {currentTableData.map(loc => (
                            <tr key={loc.id} className="hover:bg-gray-50">
                                <td className="p-4"><span className="bg-emerald-50 text-emerald-700 px-2 py-1 rounded text-xs font-bold">{loc.primary_name}</span></td>
                                <td className="p-4 font-medium">{loc.location_name}</td>
                                <td className="p-4 text-[10px] text-gray-400 font-mono">{loc.latitude},<br />{loc.longitude}</td>
                                <td className="p-4 text-xs text-gray-400 max-w-xs truncate">{loc.address}</td>
                                <td className="p-4 text-right space-x-2 whitespace-nowrap">
                                    <button onClick={() => { setFormData(loc); setShowModal(true) }} className="text-blue-500 p-2 hover:bg-blue-50 rounded-lg"><Edit size={18} /></button>
                                    <button onClick={() => deleteLocation(loc.id)} className="text-rose-500 p-2 hover:bg-rose-50 rounded-lg"><Trash2 size={18} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile Card View */}
            <div className="grid grid-cols-1 gap-4 md:hidden">
                {currentTableData.map(loc => (
                    <div key={loc.id} className="border rounded-xl p-4 space-y-2">
                        <div className="flex justify-between">
                            <span className="bg-emerald-50 text-emerald-700 px-2 py-1 rounded text-[10px] font-bold uppercase">{loc.primary_name}</span>
                            <div className="flex gap-2">
                                <button onClick={() => { setFormData(loc); setShowModal(true) }} className="text-blue-500"><Edit size={18} /></button>
                                <button onClick={() => deleteLocation(loc.id)} className="text-rose-500"><Trash2 size={18} /></button>
                            </div>
                        </div>
                        <p className="font-bold">{loc.location_name}</p>
                        <p className="text-[10px] text-gray-400">Lat: {loc.latitude} | Lng: {loc.longitude}</p>
                        <p className="text-xs text-gray-500">{loc.address}</p>
                    </div>
                ))}
            </div>

            <Pagination totalItems={filteredData.length} itemsPerPage={itemsPerPage} currentPage={currentPage} setCurrentPage={setCurrentPage} />

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
                    <form onSubmit={handleFormSubmit} className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto shadow-2xl">
                        <div className="flex justify-between border-b pb-2">
                            <h4 className="font-bold text-lg">Location Details</h4>
                            <X className="cursor-pointer text-gray-400 hover:text-black" onClick={() => setShowModal(false)} />
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Category</label>
                                <select className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none" value={formData.primary_name} onChange={e => setFormData({ ...formData, primary_name: e.target.value })}>
                                    <option>Airport</option><option>Railway Station</option><option>Seaport</option><option>Other</option>
                                </select>
                            </div>

                            {formData.primary_name === 'Other' && (
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Custom Category Name</label>
                                    <input required placeholder="e.g. Hospital" className="w-full border p-3 rounded-xl" value={formData.custom_primary} onChange={e => setFormData({ ...formData, custom_primary: e.target.value })} />
                                </div>
                            )}

                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Display Name</label>
                                <input placeholder="e.g. Heathrow Terminal 5" required className="w-full border p-3 rounded-xl" value={formData.location_name || ''} onChange={e => setFormData({ ...formData, location_name: e.target.value })} />
                            </div>

                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Search Address</label>
                                <GoogleInput
                                    value={formData.address}
                                    className="px-4 py-3 rounded-xl border border-gray-200 w-full focus:ring-2 focus:ring-emerald-500 outline-none"
                                    onSelect={(p) => setFormData({ 
                                        ...formData, 
                                        address: p.address, 
                                        latitude: p.lat, 
                                        longitude: p.lng 
                                    })}
                                />
                                {formData.latitude && (
                                    <p className="text-[9px] text-emerald-600 mt-1 font-mono">
                                        Coordinates Captured: {formData.latitude}, {formData.longitude}
                                    </p>
                                )}
                            </div>
                        </div>

                        <button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-xl font-bold shadow-lg shadow-emerald-100 transition-all mt-4">
                            Save Location
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
};

// --- DRIVER MANAGER ---
const DriverManager = () => {
    const [drivers, setDrivers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({});
    const itemsPerPage = 5;

    useEffect(() => { fetchDrivers(); }, []);

    const fetchDrivers = async () => {
        const res = await axios.get(`${APIENDPOINT}driver.php?action=list`);
        setDrivers(res.data);
    };

    const filteredData = drivers.filter(d =>
        d.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.phone.includes(searchTerm)
    );

    const currentTableData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <div>
            <TableControls searchTerm={searchTerm} setSearchTerm={setSearchTerm} onAdd={() => { setFormData({}); setShowModal(true) }} addLabel="Add Driver" />

            {/* Desktop View */}
            <div className="hidden md:block">
                <table className="w-full text-left">
                    <thead className="bg-gray-50 text-xs font-bold text-gray-400 uppercase">
                        <tr><th className="p-4">Driver Info</th><th className="p-4">Contact</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th></tr>
                    </thead>
                    <tbody className="divide-y">
                        {currentTableData.map(d => (
                            <tr key={d.id} className="hover:bg-gray-50">
                                <td className="p-4">
                                    <p className="font-bold">{d.name}</p>
                                    <p className="text-[10px] text-gray-400">UID: {d.user_name}</p>
                                </td>
                                <td className="p-4 text-sm">{d.email}<br />{d.phone}</td>
                                <td className="p-4">
                                    <span className={cn("px-2 py-1 rounded-full text-[10px] font-bold uppercase", d.is_active ? "bg-emerald-100 text-emerald-600" : "bg-gray-100 text-gray-400")}>
                                        {d.is_active ? "Active" : "Disabled"}
                                    </span>
                                </td>
                                <td className="p-4 text-right space-x-2">
                                    <button onClick={async () => {
                                        await axios.post(`${APIENDPOINT}driver.php?action=toggleActive`, { id: d.id, is_active: d.is_active ? 0 : 1 });
                                        fetchDrivers();
                                    }} className={d.is_active ? "text-orange-500" : "text-emerald-500"}>
                                        {d.is_active ? <PowerOff size={18} /> : <Power size={18} />}
                                    </button>
                                    <button onClick={() => { setFormData(d); setShowModal(true) }} className="text-blue-500"><Edit size={18} /></button>
                                    <button onClick={async () => { if (window.confirm("Delete?")) { await axios.get(`${APIENDPOINT}driver.php?action=delete&id=${d.id}`); fetchDrivers(); } }} className="text-rose-500"><Trash2 size={18} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile View */}
            <div className="md:hidden space-y-4">
                {currentTableData.map(d => (
                    <div key={d.id} className="border rounded-xl p-4">
                        <div className="flex justify-between items-start mb-2">
                            <div>
                                <p className="font-bold">{d.name}</p>
                                <p className="text-xs text-gray-500">{d.phone}</p>
                            </div>
                            <span className={cn("px-2 py-1 rounded-full text-[9px] font-bold", d.is_active ? "bg-emerald-50 text-emerald-600" : "bg-gray-100")}>
                                {d.is_active ? "ACTIVE" : "DISABLED"}
                            </span>
                        </div>
                        <div className="flex justify-end gap-4 pt-2 border-t">
                            <button onClick={() => { setFormData(d); setShowModal(true) }} className="text-blue-500 text-xs flex items-center gap-1"><Edit size={14} /> Edit</button>
                            <button onClick={async () => {
                                await axios.post(`${APIENDPOINT}driver.php?action=toggleActive`, { id: d.id, is_active: d.is_active ? 0 : 1 });
                                fetchDrivers();
                            }} className="text-orange-500 text-xs flex items-center gap-1"><Power size={14} /> Status</button>
                        </div>
                    </div>
                ))}
            </div>

            <Pagination totalItems={filteredData.length} itemsPerPage={itemsPerPage} currentPage={currentPage} setCurrentPage={setCurrentPage} />

            {/* Driver Modal - Similar structure to Location Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
                    <form onSubmit={async (e) => {
                        e.preventDefault();
                        // Ensure user_type is set (1 for Admin, 2 for Driver)
                        const payload = {
                            ...formData,
                            user_type: formData.user_type || 2
                        };
                        await axios.post(`${APIENDPOINT}driver.php?action=save`, payload);
                        setShowModal(false);
                        fetchDrivers();
                        toast.success("Driver/Admin Saved");
                    }} className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4">

                        <h4 className="font-bold text-lg border-b pb-2">Driver/Admin Details</h4>

                        <div className="space-y-4">
                            <input placeholder="Full Name" required className="w-full border p-3 rounded-xl" value={formData.name || ''} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                            <input placeholder="Login Username" required className="w-full border p-3 rounded-xl" value={formData.user_name || ''} onChange={e => setFormData({ ...formData, user_name: e.target.value })} />
                            <input placeholder="Email" type="email" required className="w-full border p-3 rounded-xl" value={formData.email || ''} onChange={e => setFormData({ ...formData, email: e.target.value })} />
                            <input placeholder="Phone" required className="w-full border p-3 rounded-xl" value={formData.phone || ''} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
                            <input placeholder="Password" type="password" className="w-full border p-3 rounded-xl" onChange={e => setFormData({ ...formData, password: e.target.value })} />

                            {/* --- NEW CHECKBOX --- */}
                            <label className="flex items-center gap-3 p-3 border rounded-xl cursor-pointer hover:bg-gray-50">
                                <input
                                    type="checkbox"
                                    className="w-5 h-5 accent-emerald-600"
                                    checked={formData.user_type == 1}
                                    onChange={(e) => setFormData({ ...formData, user_type: e.target.checked ? 1 : 2 })}
                                />
                                <div className="flex flex-col">
                                    <span className="font-bold text-sm">Make as Admin</span>
                                    <span className="text-[10px] text-gray-400">Grant administrative access</span>
                                </div>
                            </label>
                        </div>

                        <button className="w-full bg-emerald-600 text-white py-4 rounded-xl font-bold">Save Details</button>
                        <button type="button" onClick={() => setShowModal(false)} className="w-full text-gray-400 text-sm">Cancel</button>
                    </form>
                </div>
            )}
        </div>
    );
};

const PriceList = () => {
    const [pricing, setPricing] = useState({
        id: null,
        base_distance: 2.0,
        base_price: 6.0,
        price_per_mile: 2.60,
        step_distance: 0.5
    });
    const [loading, setLoading] = useState(false);
    const [testMiles, setTestMiles] = useState(5);

    useEffect(() => {
        const fetchPricing = async () => {
            try {
                const res = await fetch(`${APIENDPOINT}booking.php?action=getPricing`);
                const data = await res.json();
                if (data.id) setPricing(data);
            } catch (err) { console.error("Fetch error:", err); }
        };
        fetchPricing();
    }, []);

    const handleSave = async () => {
        setLoading(true);
        const loadingToast = toast.loading("Saving pricing...");
        try {
            const res = await fetch(`${APIENDPOINT}booking.php?action=updatePricing`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(pricing)
            });
            if (res.ok) toast.success("Pricing updated!", { id: loadingToast });
            else throw new Error();
        } catch (err) {
            toast.error("Error saving.", { id: loadingToast });
        } finally { setLoading(false); }
    };

    const calculatePreview = () => {
        const miles = parseFloat(testMiles) || 0;
        const { base_distance, base_price, price_per_mile, step_distance } = pricing;
        const roundedMiles = Math.ceil(miles / step_distance) * step_distance;
        if (roundedMiles <= base_distance) return parseFloat(base_price).toFixed(2);
        const extraMiles = roundedMiles - base_distance;
        const totalFare = parseFloat(base_price) + (extraMiles * parseFloat(price_per_mile));
        return totalFare.toFixed(2);
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between border-b pb-4">
                <div>
                    <h2 className="text-xl font-bold text-gray-900">Pricing Configuration</h2>
                    <p className="text-xs text-gray-500">Set base rates and per-mile charges</p>
                </div>
                <Info className="text-blue-400 w-5 h-5" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <PriceInput label="Base Distance (Miles)" icon={Ruler} value={pricing.base_distance} onChange={(v) => setPricing({ ...pricing, base_distance: v })} desc="Min distance included in base price." />
                <PriceInput label="Base Price (£)" icon={PoundSterling} value={pricing.base_price} onChange={(v) => setPricing({ ...pricing, base_price: v })} desc="Starting fare for any booking." />
                <PriceInput label="Price Per Mile (£)" icon={ArrowRight} value={pricing.price_per_mile} onChange={(v) => setPricing({ ...pricing, price_per_mile: v })} desc="Cost per mile after base distance." />
                <PriceInput label="Step Increment (Miles)" icon={Info} value={pricing.step_distance} onChange={(v) => setPricing({ ...pricing, step_distance: v })} desc="Rounding logic increment." />
            </div>

            <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-100 space-y-4">
                <div className="flex items-center gap-2 text-emerald-700 font-bold text-sm"><Calculator size={18} /><span>Fare Tester</span></div>
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1">
                        <label className="text-[10px] uppercase font-bold text-emerald-600 block mb-1">Miles to test</label>
                        <input type="number" step="0.01" value={testMiles} onChange={(e) => setTestMiles(e.target.value)} className="w-full bg-white border border-emerald-200 rounded-xl px-4 py-2 text-lg font-bold outline-none focus:ring-2 focus:ring-emerald-500" />
                    </div>
                    <div className="text-center md:text-right">
                        <label className="text-[10px] uppercase font-bold text-emerald-600 block mb-1">Estimated Fare</label>
                        <span className="text-3xl font-black text-emerald-900">£{calculatePreview()}</span>
                    </div>
                </div>
            </div>

            <button onClick={handleSave} disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-2xl font-bold flex items-center justify-center gap-3 transition-all disabled:opacity-50">
                {loading ? "Saving..." : <><Save size={20} /> Update Pricing</>}
            </button>
        </div>
    );
};

const PriceInput = ({ label, value, onChange, desc, icon: Icon }) => (
    <div className="space-y-2">
        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
            <Icon size={14} className="text-gray-400" />{label}
        </label>
        <input type="number" step="0.01" value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 outline-none font-bold text-gray-700 transition-all" />
        <p className="text-[10px] text-gray-400 leading-tight">{desc}</p>
    </div>
);

// --- APP SETTINGS PLACEHOLDER ---
const AppSettings = () => (
    <div className="space-y-6">
        <h3 className="text-lg font-bold">App Settings</h3>
        <div className="grid grid-cols-1 gap-4">
            <div className="p-4 border rounded-xl flex items-center justify-between">
                <div>
                    <p className="font-bold">Maintenance Mode</p>
                    <p className="text-xs text-gray-500">Disable bookings for all users</p>
                </div>
                <input type="checkbox" className="w-5 h-5 accent-emerald-600" />
            </div>
            <div className="p-4 border rounded-xl flex items-center justify-between">
                <div>
                    <p className="font-bold">Email Notifications</p>
                    <p className="text-xs text-gray-500">Send automated emails for every booking</p>
                </div>
                <input type="checkbox" defaultChecked className="w-5 h-5 accent-emerald-600" />
            </div>
        </div>
    </div>
);